import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Condition } from '../../proto/generated/nexus'
import { Security } from '../../proto/generated/security'
import { AppContext } from '../../App'
import { ChartAlertSnapshot } from '../alerts/alertModal.typings'
import { selectActiveChartId, selectIndicators } from '../../selectors/chart'
import { selectQuickOrderData } from '../../selectors/orders'
import { selectBasketId } from '../../selectors/strategyBuilder'
import { AlertModalPurpose, removeAlertModalData, setAlertModalData } from '../../reducer/alerts.reducer'
import {
    buildQuickTradeChartSnapshot,
    conditionalPrimaryUsesSpreadLhs,
    ConditionalEntryState,
    hasConditionalRuleContent,
    mergeQuickTradeSnapshotWithMatchingChart,
    summarizeConditionalEntry,
    userAlertForConditionalEntryModal,
} from '../../util/conditionalEntry'
import {
    clearConditionalEntryAlertModalHandlers,
    registerConditionalEntryAlertModalHandlers,
} from '../../util/conditionalEntryAlertModalBridge'

export type QuickTradeConditionalVariant = 'entry' | 'exit' | 'modify'

export type QuickTradeConditionalEntryProps = {
    security: Security
    displaySymbol: string
    /** Last traded price in native (exchange) units — same as chart `data.close` / order wire. */
    lastPrice: number
    lastVolume: number
    priceMultiplier: number
    disabled: boolean
    variant?: QuickTradeConditionalVariant
    allowEnableConditionalEntry?: boolean
    conditionSecurity?: Security
    /** Basket: open CE leg — cannot clear without cancel. */
    lockRemoveCondition?: boolean
    /**
     * Quick trade modify: true if this order (or basket CE sibling) already had a conditional rule before this edit.
     * When false (e.g. normal order with no prior rule), user may clear the condition before placing.
     */
    persistedOrderHadCondition?: boolean
    /** Overrides checkbox + dialog title (e.g. modify row). */
    labelOverride?: string
    value: ConditionalEntryState
    onChange: (next: ConditionalEntryState) => void
    compact?: boolean
    /** Trade panel advanced section: match iceberg (caption label, checkbox spacing). */
    tradePanelAdvanced?: boolean
    /**
     * Basket: strategy spread snapshot (built with zeroed OHLC/volume). Enables Underlying vs Strategy in the alert dialog.
     */
    conditionalModalChartVariants?: {
        spreadSnapshot: ChartAlertSnapshot
        initialRef: 'underlying' | 'spread'
    }
}

const QuickTradeConditionalEntry: React.FC<QuickTradeConditionalEntryProps> = ({
    security,
    displaySymbol,
    lastPrice,
    lastVolume,
    priceMultiplier,
    disabled,
    variant = 'entry',
    allowEnableConditionalEntry = true,
    conditionSecurity,
    lockRemoveCondition = false,
    persistedOrderHadCondition = false,
    labelOverride,
    value,
    onChange,
    compact = false,
    tradePanelAdvanced = false,
    conditionalModalChartVariants,
}) => {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const indicators = useSelector(selectIndicators)
    const quickOrder = useSelector(selectQuickOrderData)
    const strategyBasketId = useSelector(selectBasketId)
    const lhsSecurity = conditionSecurity ?? security

    const valueRef = useRef(value)
    valueRef.current = value

    const modalPurpose: AlertModalPurpose =
        variant === 'exit' ? 'conditionalExit' : variant === 'modify' ? 'conditionalModify' : 'conditionalEntry'

    const checkboxLabel =
        labelOverride ??
        (variant === 'exit' ? 'CONDITIONAL EXIT' : variant === 'entry' ? 'CONDITIONAL ENTRY' : 'CONDITION')

    const openDialog = useCallback(() => {
        if (!allowEnableConditionalEntry && !hasConditionalRuleContent(value.condition)) return
        const chartFn = appContext.getChartSnapshot[activeChartId]
        const chartSnap =
            typeof chartFn === 'function'
                ? (chartFn() as ChartAlertSnapshot | undefined)
                : undefined

        const spreadSnap = conditionalModalChartVariants?.spreadSnapshot
        const hasBasketModalVariants = !!(conditionalModalChartVariants && spreadSnap)

        const seed: Condition | undefined = hasConditionalRuleContent(value.condition)
            ? value.condition
            : undefined

        let snap: ChartAlertSnapshot
        let modalVariantPayload:
            | {
                  conditionalModalChartVariants: {
                      underlying: ChartAlertSnapshot
                      spread: ChartAlertSnapshot | null
                  }
                  conditionalModalChartRef: 'underlying' | 'spread'
              }
            | undefined

        if (hasBasketModalVariants) {
            const underlyingBase = buildQuickTradeChartSnapshot(lhsSecurity, 0, 0)
            const uMerged = mergeQuickTradeSnapshotWithMatchingChart(underlyingBase, chartSnap, lhsSecurity)
            const sMerged = mergeQuickTradeSnapshotWithMatchingChart(spreadSnap!, chartSnap, lhsSecurity)
            const initialRef: 'underlying' | 'spread' =
                seed != null
                    ? conditionalPrimaryUsesSpreadLhs(seed)
                        ? 'spread'
                        : 'underlying'
                    : conditionalModalChartVariants!.initialRef
            snap = initialRef === 'spread' ? sMerged : uMerged
            modalVariantPayload = {
                conditionalModalChartVariants: { underlying: uMerged, spread: sMerged },
                conditionalModalChartRef: initialRef,
            }
        } else {
            const baseSnap = buildQuickTradeChartSnapshot(lhsSecurity, lastPrice, lastVolume)
            snap = mergeQuickTradeSnapshotWithMatchingChart(baseSnap, chartSnap, lhsSecurity)
        }

        const alert = userAlertForConditionalEntryModal(snap, seed)

        const basketFromTradePanel = tradePanelAdvanced && Boolean((strategyBasketId ?? '').trim())
        const basketFromQuickTradeWindow = Boolean(
            quickOrder?.openDialog && quickOrder?.order?.isBasket
        )

        registerConditionalEntryAlertModalHandlers(
            (condition: Condition) => {
                onChange({ enabled: true, condition })
            },
            () => {
                const v = valueRef.current
                if (v.enabled && !hasConditionalRuleContent(v.condition)) {
                    onChange({ enabled: false, condition: undefined })
                }
            }
        )

        dispatch(
            setAlertModalData({
                chartSnapshot: snap,
                alert,
                edit: false,
                purpose: modalPurpose,
                conditionalUseFullIndicatorCatalog:
                    hasBasketModalVariants ||
                    basketFromTradePanel ||
                    basketFromQuickTradeWindow,
                ...modalVariantPayload,
            })
        )
    }, [
        allowEnableConditionalEntry,
        value.condition,
        lhsSecurity,
        lastPrice,
        lastVolume,
        onChange,
        dispatch,
        modalPurpose,
        appContext.getChartSnapshot,
        activeChartId,
        conditionalModalChartVariants,
        quickOrder?.openDialog,
        quickOrder?.order?.isBasket,
        strategyBasketId,
        tradePanelAdvanced,
    ])

    const summary = useMemo(
        () =>
            summarizeConditionalEntry(value.condition, displaySymbol, {
                indicators,
            }),
        [value.condition, displaySymbol, indicators]
    )

    const forbidRemoveCondition = lockRemoveCondition || persistedOrderHadCondition

    const clearCondition = useCallback(() => {
        dispatch(removeAlertModalData())
        clearConditionalEntryAlertModalHandlers()
        onChange({ enabled: false, condition: undefined })
    }, [onChange, dispatch])

    useEffect(() => () => clearConditionalEntryAlertModalHandlers(), [])

    const onToggle = useCallback(
        (_: unknown, on: boolean) => {
            if (!on) {
                if (forbidRemoveCondition && hasConditionalRuleContent(value.condition)) {
                    return
                }
                clearCondition()
                return
            }
            if (!allowEnableConditionalEntry) return
            onChange({ enabled: true })
            openDialog()
        },
        [onChange, openDialog, allowEnableConditionalEntry, forbidRemoveCondition, value.condition, clearCondition]
    )

    const summaryFontSize = compact ? '0.7rem' : '0.75rem'
    const btnSx = { fontSize: compact ? '0.7rem' : '0.75rem', minWidth: 'auto', py: 0.25, px: 0.75 }

    const checkboxDisabled =
        disabled ||
        !allowEnableConditionalEntry ||
        (forbidRemoveCondition && hasConditionalRuleContent(value.condition))

    const control = (
        <Checkbox
            checked={value.enabled}
            onChange={(_, c) => onToggle(_, c)}
            disabled={checkboxDisabled}
            size="small"
            sx={{ mr: 0.5, p: 0 }}
            inputProps={{ 'aria-label': checkboxLabel }}
        />
    )

    return (
            <Stack
                spacing={tradePanelAdvanced ? 1 : 0.75}
                sx={{ width: '100%', ...(tradePanelAdvanced ? { minWidth: 0, maxWidth: '100%' } : {}) }}
            >
                <FormControlLabel
                    sx={
                        tradePanelAdvanced
                            ? { m: 0, mr: 0, alignItems: 'center' }
                            : {
                                  m: 0,
                                  mr: 0,
                                  alignItems: 'center',
                                  gap: 0.5,
                                  '& .MuiFormControlLabel-label': {
                                      fontSize: compact ? '0.7rem' : '0.75rem',
                                      fontWeight: 600,
                                      letterSpacing: '0.04em',
                                      color: 'text.secondary',
                                  },
                              }
                    }
                    control={control}
                    label={
                        tradePanelAdvanced ? (
                            <Typography variant="caption" color="text.secondary">
                                {checkboxLabel}
                            </Typography>
                        ) : (
                            checkboxLabel
                        )
                    }
                />
            {value.enabled && hasConditionalRuleContent(value.condition) ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: '8px 10px',
                            bgcolor: 'action.hover',
                            borderStyle: 'dashed',
                            borderColor: 'divider',
                            overflow: 'hidden',
                            maxWidth: '100%',
                            minWidth: 0,
                        }}
                    >
                        <Stack spacing={0.75} sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                title={tradePanelAdvanced ? summary : undefined}
                                sx={{
                                    fontSize: summaryFontSize,
                                    lineHeight: 1.35,
                                    wordBreak: 'break-word',
                                    overflow: 'hidden',
                                    maxWidth: '100%',
                                    ...(tradePanelAdvanced
                                        ? {
                                              display: '-webkit-box',
                                              WebkitBoxOrient: 'vertical',
                                              WebkitLineClamp: 2,
                                              textOverflow: 'ellipsis',
                                          }
                                        : {}),
                                }}
                            >
                                {summary}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={disabled || !allowEnableConditionalEntry}
                                    onClick={openDialog}
                                    sx={btnSx}
                                >
                                    Edit
                                </Button>
                                {!forbidRemoveCondition ? (
                                    <Button size="small" color="inherit" disabled={disabled} onClick={clearCondition} sx={btnSx}>
                                        Delete
                                    </Button>
                                ) : null}
                            </Box>
                        </Stack>
                    </Paper>
                ) : null}
            </Stack>
    )
}

export default memo(QuickTradeConditionalEntry)
